/* Mantrana — 5-question psychological decision profiler (pool-based).
 *
 * Each life-domain has a POOL of 20 domain-specific, emotionally-grounded
 * questions organized into 5 buckets of 4 (one bucket per psychological
 * dimension). At session start we randomly sample ONE question from each
 * bucket → the user always sees 5 fresh, dimension-complete questions.
 *
 * Downstream aggregation is language-agnostic and deterministic — each
 * option carries an archetype index (0..3) which maps to a (label, score)
 * pair defined in mcqPool.js.
 */

import { DIM_META, DIMENSIONS, DIM_TO_ARCHETYPE, POOL_EN } from "./mcqPool";
import { POOL_HI } from "./mcqPool_hi";

export const CATEGORIES = [
  "Career",
  "Relationships",
  "Money",
  "Family",
  "Purpose",
  "Major Decision",
];

export { DIMENSIONS };

export const DIMENSION_LABELS = {
  decision_confidence: "Decision Confidence",
  emotional_clarity:   "Emotional Clarity",
  risk_readiness:      "Risk Readiness",
  purpose_alignment:   "Purpose Alignment",
  self_trust:          "Self-Trust",
};

/* Random integer in [0, n). Deterministic if `seed` is passed (simple LCG). */
function pickIndex(n, seed) {
  if (seed == null) return Math.floor(Math.random() * n);
  // simple mulberry32
  let t = (seed + 0x6D2B79F5) | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return Math.abs((t ^ (t >>> 14)) >>> 0) % n;
}

/* Sample 5 questions — one per dimension — from the language-appropriate
 * pool for `category`. Returns questions in the fixed dimension order:
 *   [decision_confidence, emotional_clarity, risk_readiness,
 *    purpose_alignment, self_trust]
 * Each question is normalized to:
 *   { dim, arch, q, options: [ { text, letter, label, scores, tags } ×4 ] }
 */
export function sampleQuestions(category, lang = "en", seedBase = null) {
  const pool = (lang === "hi" ? POOL_HI : POOL_EN)[category]
    || POOL_EN[category]
    || POOL_EN["Major Decision"];

  return DIMENSIONS.map((dim, i) => {
    const bucket = pool[dim] || [];
    const seed = seedBase == null ? null : seedBase + i * 101;
    const idx = pickIndex(bucket.length, seed);
    const raw = bucket[idx] || bucket[0];
    if (!raw) return null;
    const options = raw.o.map(([text, ai], oi) => {
      const [label, score] = DIM_META[dim][ai] || ["", 0];
      return {
        letter: ["A", "B", "C", "D"][oi],
        label,
        text,
        scores: { [dim]: score },
        // legacy `tags` shape for any old consumers
        tags: { [dim]: label },
      };
    });
    return {
      dim,
      arch: DIM_TO_ARCHETYPE[dim],
      dimension: dim,          // legacy alias
      archetype: DIM_TO_ARCHETYPE[dim],
      q: raw.q,
      options,
    };
  }).filter(Boolean);
}

/* Aggregate the user's answers (indices into each question's options) into
 * a 5-dimension psychological profile. `questions` MUST be the exact set
 * that was shown to the user (returned by sampleQuestions).
 */
export function buildProfile(category, answers, questions) {
  const dimensions = {};
  const labels = {};
  const letters = {};
  const raw_counts = {};

  (questions || []).forEach((question, qi) => {
    const optIdx = answers[qi];
    if (optIdx == null || optIdx < 0) return;
    const opt = question.options[optIdx];
    if (!opt) return;

    letters[question.arch] = opt.letter;
    labels[question.dim] = opt.label;
    raw_counts[question.arch] = { [opt.label]: 1 };
    if (opt.scores) {
      Object.entries(opt.scores).forEach(([dim, s]) => {
        dimensions[dim] = s;
      });
    }
  });

  return { category, dimensions, labels, letters, raw_counts };
}

/* Compact narrative summary sent to the LLM. */
export function profileToChallenge(category, profile) {
  const d = profile.dimensions || {};
  const l = profile.labels || {};
  const bits = [
    `Category: ${category}.`,
    l.decision_confidence
      ? `Decision style: ${l.decision_confidence} (Decision Confidence ${d.decision_confidence ?? "-"}/100).`
      : "",
    l.emotional_clarity
      ? `Emotional pattern under stress: ${l.emotional_clarity} (Emotional Clarity ${d.emotional_clarity ?? "-"}/100).`
      : "",
    l.risk_readiness
      ? `Risk orientation: ${l.risk_readiness} (Risk Readiness ${d.risk_readiness ?? "-"}/100).`
      : "",
    l.purpose_alignment
      ? `Core motivation: ${l.purpose_alignment} (Purpose Alignment ${d.purpose_alignment ?? "-"}/100).`
      : "",
    l.self_trust
      ? `Hidden conflict: ${l.self_trust} (Self-Trust ${d.self_trust ?? "-"}/100).`
      : "",
  ].filter(Boolean);
  return bits.join(" ");
}

/* -------- Legacy compatibility -------- *
 * Some older components imported getLocalizedMcqBank / MCQ_BANK. These no
 * longer power the flow but we keep a shape-compatible export so any
 * stragglers don't blow up. */
export const MCQ_BANK = Object.fromEntries(
  CATEGORIES.map((c) => [c, sampleQuestions(c, "en", 1)]),
);

export function getLocalizedMcqBank(lang) {
  return Object.fromEntries(
    CATEGORIES.map((c) => [c, sampleQuestions(c, lang, 1)]),
  );
}
